<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { APP_EVENTS, appEventBus } from '@/Utils/appEventBus'
import { useProjectSwitch, consumeProjectSwitchMemo } from '@/composables/useProjectSwitch'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconGear from '@/components/icon/IconGear.vue'
import IconTeam from '@/components/icon/IconTeam.vue'
import Spinner from '@/components/ui/Spinner.vue'
import ProjectTile from './ProjectTile.vue'
import ProjectSwitchOverlay from './ProjectSwitchOverlay.vue'
import CreateProjectModal from './CreateProjectModal.vue'
import ProjectLimitModal from './ProjectLimitModal.vue'
import ManageProjectsModal from './ManageProjectsModal.vue'

const props = defineProps({
  expanded: {
    type: Boolean,
    default: true,
  },
})

const store = useStore()
const router = useRouter()
const { notify } = useNotification()
const { switching, pendingProject, switchTo } = useProjectSwitch()

const projects = computed(() => store.getters['Projects/projects'])
const activeProject = computed(() => store.getters['Projects/activeProject'])
const allowance = computed(() => store.getters['Projects/projectAllowance'])
const canCreate = computed(() => store.getters['Projects/canCreateProject'])
const isOwner = computed(() => store.getters['Projects/isProjectOwner'])
const showSwitcher = computed(() => store.getters['Projects/showProjectSwitcher'])
const hasMultiple = computed(() => store.getters['Projects/hasMultipleProjects'])

// Whether the session is inside someone else's team. A member granted no
// workspace (or only paused ones) gets an empty list and simply no row here:
// each page already explains the missing permission in its own content area.
const inTeam = computed(() => !!store.state.User?.activatedTeam)

const dropdown = ref(null)
const createModalOpen = ref(false)
const limitModalOpen = ref(false)
const manageModalOpen = ref(false)

const displayProject = computed(() => (
  switching.value && pendingProject.value ? pendingProject.value : activeProject.value
))
const displayName = computed(() => displayProject.value?.name || 'Workspace')
const displayColor = computed(() => displayProject.value?.color || '#3b82f6')

const roleLabel = (role) => {
  const key = String(role || '').toLowerCase()
  if (!key || key === 'owner') return ''
  return key.charAt(0).toUpperCase() + key.slice(1)
}

// A member with one workspace has nothing to switch to, so the control becomes a
// plain label: it still says which client's content they are looking at, and
// the trailing role says what they can do there.
const isStaticLabel = computed(() => !isOwner.value && !hasMultiple.value)
const activeRole = computed(() => (isOwner.value ? '' : roleLabel(activeProject.value?.role)))

// Menu header count: the owner's allowance, or how many the member can enter.
const countLabel = computed(() => {
  const limit = Number(allowance.value.limit) || 0
  if (isOwner.value) return limit ? `${allowance.value.used} of ${limit}` : ''
  return projects.value.length > 1 ? String(projects.value.length) : ''
})

// Owners always may; a member only with the invite right in this account
// (admins carry it as `teamInviteUsers`). The shortcut lands on the Team page
// with the invite sheet open, since the sheet needs the roster that lives there.
const memberPermission = computed(() => store.state.User?.memberPermission)
const canInvite = computed(() => {
  if (!inTeam.value) return isOwner.value
  const perm = memberPermission.value
  return !!perm && perm.isEnabled !== false && perm.role === 'admin' && !!perm.teamInviteUsers
})

const isActive = (project) => project.id === activeProject.value?.id
const itemTitle = (project) => {
  if (isActive(project)) return `${project.name} (current)`
  return `Switch to ${project.name}`
}

// One slim row: tile, name, chevron. Collapsed, only the tile remains.
// `flex` rather than `inline-flex` on the collapsed square: the wrapper fills
// the rail, so the tile is centred by its own auto margins, and auto margins do
// nothing to an inline-level box. Inline, it sat against the left edge while the
// nav icons below — a `w-fit mx-auto` list — sat in the middle.
const dropdownClasses = computed(() => (
  props.expanded
    ? '!w-full flex items-center gap-2 !rounded-lg !px-1.5 !py-1 !h-9 box-border hover:no-underline hover:bg-surface-2 transition-colors ring-1 ring-transparent hover:ring-surface-3'
    : 'size-9 flex items-center justify-center !rounded-lg bg-surface-2 hover:bg-surface-3 transition-colors box-border hover:no-underline !p-0 mx-auto'
))

const itemClasses = '!h-auto !py-2 !px-2.5 gap-x-2.5 hover:bg-surface-3 text-surface-text-1 hover:text-white flex items-center w-full rounded-md transition-colors'

const openInvite = () => {
  dropdown.value?.hide?.()
  router.push({ path: '/manage/team', query: { invite: '1' } })
}

/**
 * The reload that completes a switch wipes every toast, so the confirmation
 * is shown by the *next* page, once it knows which workspace it landed in.
 */
const announceLandedSwitch = () => {
  const memo = consumeProjectSwitchMemo()
  if (!memo) return
  const landed = activeProject.value
  if (!landed || landed.id !== memo.id) return
  notify(memo.created
    ? { group: 'success', title: `${landed.name} is ready`, text: 'It starts empty — create a stream or upload a video to get going.' }
    : { group: 'info', text: `You're now in ${landed.name}.` })
}

let onTeamChanged
onMounted(async () => {
  await store.dispatch('Projects/loadProjects')
  announceLandedSwitch()

  // Workspaces belong to the account that owns the resources. Switching into a
  // team makes the user a guest there, and the server answers with the
  // workspaces they were granted in THAT account, so the switcher has to re-ask
  // rather than keep showing the workspaces of the account they just left.
  onTeamChanged = () => store.dispatch('Projects/loadProjects', true)
  appEventBus.on(APP_EVENTS.TEAM_CHANGED, onTeamChanged)
})

onBeforeUnmount(() => {
  appEventBus.off(APP_EVENTS.TEAM_CHANGED, onTeamChanged)
})

const openCreate = () => {
  dropdown.value?.hide?.()
  if (canCreate.value) {
    createModalOpen.value = true
  } else {
    limitModalOpen.value = true
  }
}

const openManage = () => {
  dropdown.value?.hide?.()
  manageModalOpen.value = true
}

// One modal handing over to another: wait for the first one's leave
// transition, otherwise its cleanup unlocks body scroll under the second.
const afterModalClose = (open) => setTimeout(open, 220)

const changeProject = async (project) => {
  dropdown.value?.hide?.()
  if (!project?.id || project.id === activeProject.value?.id) return
  try {
    await switchTo(project)
  } catch (error) {
    // 403: the grant was revoked or paused since the list was loaded. Say so,
    // then re-read the list so the workspace the member can no longer enter
    // disappears instead of failing again on the next click.
    if (error?.code === 403) {
      notify({
        group: 'error',
        title: `You no longer have access to ${project.name}`,
        text: 'Ask the account owner if you need it back.',
      })
      store.dispatch('Projects/loadProjects', true)
      return
    }
    notify({
      group: 'error',
      title: `Couldn't switch to ${project.name}`,
      text: error?.message || 'Please try again.',
    })
  }
}

const onCreated = async (project, { open } = {}) => {
  if (!open) {
    notify({
      group: 'success',
      title: `${project.name} was created`,
      text: 'Open it from the workspace menu whenever you\'re ready.',
    })
    return
  }
  // Land the owner in the workspace they just created — it is empty, which is
  // the clearest possible confirmation that the containers are separate.
  try {
    await switchTo(project, { created: true })
  } catch (error) {
    notify({
      group: 'error',
      title: `${project.name} was created, but couldn't be opened`,
      text: error?.message || 'Open it from the workspace menu.',
    })
  }
}
</script>

<template>
  <div v-if="showSwitcher" class="min-w-0">
    <!-- Single workspace, not the owner: a label, not a menu. -->
    <div
      v-if="isStaticLabel"
      class="flex items-center gap-2 rounded-lg px-1.5 h-9 box-border"
      :class="{ 'mx-auto justify-center !px-0': !expanded }"
      :aria-label="`Workspace: ${displayName}`"
      data-testid="project-label"
    >
      <ProjectTile
        :name="displayName"
        :color="displayColor"
        size="size-7"
        :title="expanded ? undefined : displayName"
      />
      <template v-if="expanded">
        <span class="min-w-0 flex-1 truncate text-left text-sm font-medium text-surface-8 leading-tight">{{ displayName }}</span>
        <span v-if="activeRole" class="shrink-0 text-[11px] text-surface-6">{{ activeRole }}</span>
      </template>
    </div>

    <DropDownMenu
      v-else
      ref="dropdown"
      :full-width="expanded"
      :class="{ 'mx-auto w-fit': !expanded }"
      :custom-classes="dropdownClasses"
      wrapper-class="min-w-[252px]"
      max-height="80"
      :aria-label="`Workspace: ${displayName}`"
    >
      <template #toggle-button>
        <ProjectTile
          :name="displayName"
          :color="displayColor"
          size="size-7"
          :title="expanded ? undefined : displayName"
        />
        <span v-if="expanded" class="min-w-0 flex-1 truncate text-left text-sm font-medium text-surface-8 leading-tight">
          {{ displayName }}
        </span>
        <Spinner
          v-if="expanded && switching"
          classes="shrink-0"
          spinner-color="var(--c-surface-6)"
          spinner-size="14px"
        />
        <icon-base v-else-if="expanded" class="shrink-0 text-surface-6">
          <icon-chevron-up-down />
        </icon-base>
      </template>

      <template #menu-items>
        <li class="flex items-baseline justify-between gap-2 px-2.5 pt-2.5 pb-1 select-none">
          <span class="text-[10px] font-semibold uppercase tracking-wider text-surface-6">
            {{ isOwner ? 'Workspaces' : 'Your workspaces' }}
          </span>
          <span v-if="countLabel" class="text-[10px] text-surface-6 tabular-nums">{{ countLabel }}</span>
        </li>

        <DropDownMenuItem
          v-for="project in projects"
          :key="project.id"
          :custom-classes="itemClasses"
          :title="itemTitle(project)"
          @click="changeProject(project)"
        >
          <ProjectTile :name="project.name" :color="project.color" size="size-6" textClass="text-[10px]" />
          <span class="flex-1 min-w-0 truncate text-left text-tiny">{{ project.name }}</span>
          <!-- Owner: which one is the default (it cannot be deleted). Member:
               what they can do in each workspace. Both are quiet trailing labels. -->
          <span
            v-if="isOwner && project.isDefault"
            class="shrink-0 rounded-full bg-surface-3 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider text-surface-7"
            title="The default workspace. It holds the account's original content and cannot be deleted."
          >Default</span>
          <span
            v-else-if="!isOwner && roleLabel(project.role)"
            class="shrink-0 text-[10px] text-surface-6"
          >{{ roleLabel(project.role) }}</span>
          <icon-base v-if="isActive(project)" class="shrink-0 text-main-light">
            <icon-checkmark />
          </icon-base>
        </DropDownMenuItem>

        <template v-if="isOwner">
          <li class="my-1 border-t border-surface-3" role="separator" />

          <DropDownMenuItem :custom-classes="itemClasses" @click="openCreate">
            <span class="size-6 shrink-0 grid place-items-center rounded-md bg-surface-3 text-surface-8">
              <icon-base :width="14" :height="14"><icon-plus /></icon-base>
            </span>
            <span class="flex-1 min-w-0 truncate text-left text-tiny">New workspace</span>
            <span v-if="!canCreate" class="shrink-0 text-[10px] uppercase tracking-wider text-surface-6">Limit</span>
          </DropDownMenuItem>

          <DropDownMenuItem :custom-classes="itemClasses" @click="openManage">
            <span class="size-6 shrink-0 grid place-items-center rounded-md bg-surface-3 text-surface-8">
              <icon-base :width="14" :height="14"><icon-gear /></icon-base>
            </span>
            <span class="flex-1 min-w-0 truncate text-left text-tiny">Manage workspaces</span>
          </DropDownMenuItem>
        </template>

        <!-- The team is one per account and access is per workspace, so the
             place to add someone is right where they pick the workspace. -->
        <template v-if="canInvite">
          <li class="my-1 border-t border-surface-3" role="separator" />
          <DropDownMenuItem :custom-classes="itemClasses" @click="openInvite">
            <span class="size-6 shrink-0 grid place-items-center rounded-md bg-surface-3 text-surface-8">
              <icon-base :width="14" :height="14" fill="none"><icon-team /></icon-base>
            </span>
            <span class="flex-1 min-w-0 truncate text-left text-tiny">Invite members</span>
          </DropDownMenuItem>
        </template>
      </template>
    </DropDownMenu>

    <ProjectSwitchOverlay :project="switching ? pendingProject : null" />

    <template v-if="isOwner">
      <CreateProjectModal v-model="createModalOpen" @created="onCreated" />
      <ProjectLimitModal v-model="limitModalOpen" @manage="afterModalClose(openManage)" />
      <ManageProjectsModal
        v-model="manageModalOpen"
        @create="afterModalClose(openCreate)"
        @switch="changeProject"
      />
    </template>
  </div>
</template>
